/* Zevari Campaign Modal
 *
 * Launched from a Hunt Mission card. Turns the mission's hunted contacts
 * (SignalContacts with a LinkedIn URL) into a Zevari LinkedIn campaign:
 *
 *   warm_up : view profile → like recent post → connect (optional note)
 *   outreach: view profile → connect (note) → follow-up message
 *
 * SAFETY: the campaign is created as a DRAFT in Zevari and activation is
 * staged behind Zevari's human approval gate. Nothing is ever sent until
 * the user approves via the returned approval_url.
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    X, Rocket, Loader2, ShieldCheck, ExternalLink, CheckCircle2,
    AlertTriangle, Users, Flame, Send,
} from "lucide-react";

const ZEV = "#38bdf8";

export default function ZevariCampaignModal({ mission, onClose, onChanged }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [info, setInfo] = useState(null);          // {configured,count,targets,existing_campaign}
    const [showForm, setShowForm] = useState(false); // force form even when a campaign exists
    const [busy, setBusy] = useState(false);
    const [activating, setActivating] = useState(false);
    const [campaign, setCampaign] = useState(null);  // existing or freshly created campaign doc
    const [name, setName] = useState(`Hunt: ${mission.title}`);
    const [type, setType] = useState("warm_up");
    const [note, setNote] = useState("");
    const [message, setMessage] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get(`/hunt-missions/${mission.id}/zevari-targets`);
                setInfo(r.data);
                if (r.data.existing_campaign) setCampaign(r.data.existing_campaign);
            } catch (e) {
                toast.error(e?.response?.data?.detail || t("signals.zev_failed"));
            } finally { setLoading(false); }
        })();
    }, [mission.id, t]);

    const create = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/hunt-missions/${mission.id}/zevari-campaign`, {
                name, campaign_type: type,
                connection_note: note.trim() || null,
                message_body: message.trim() || null,
            });
            setCampaign(r.data);
            setShowForm(false);
            toast.success(t("signals.zev_created_toast"));
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("signals.zev_failed"));
        } finally { setBusy(false); }
    };

    const activate = async () => {
        setActivating(true);
        try {
            const r = await api.post(`/hunt-missions/${mission.id}/zevari-campaign/activate`);
            setCampaign(r.data);
            toast.success(t("signals.zev_activated_toast"));
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("signals.zev_failed"));
        } finally { setActivating(false); }
    };

    const targets = info?.targets || [];
    const showStatus = campaign && !showForm;

    return (
        <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm grid place-items-center p-4" data-testid="zevari-campaign-modal">
            <div className="w-full max-w-2xl rounded-2xl border border-white/12 bg-[var(--jr-surface)] shadow-2xl max-h-[90vh] overflow-y-auto">
                <header className="px-6 py-4 border-b border-white/10 flex items-start gap-3 sticky top-0 bg-[var(--jr-surface)] z-10">
                    <div className="w-9 h-9 rounded-full grid place-items-center shrink-0" style={{ background: `${ZEV}20` }}>
                        <Rocket size={16} style={{ color: ZEV }} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-white leading-tight">{t("signals.zev_title")}</h3>
                        <p className="text-xs text-[var(--jr-text-3)] mt-0.5 truncate">{mission.title}</p>
                    </div>
                    <button onClick={onClose} data-testid="zevari-modal-close"
                        className="p-1.5 rounded-md text-[var(--jr-text-3)] hover:text-white hover:bg-white/5">
                        <X size={16} />
                    </button>
                </header>

                <div className="px-6 py-5 space-y-4">
                    {loading ? (
                        <div className="py-10 grid place-items-center text-[var(--jr-text-3)]">
                            <Loader2 className="animate-spin" size={20} />
                        </div>
                    ) : !info?.configured ? (
                        <div className="rounded-xl border border-[var(--jr-amber)]/30 bg-[var(--jr-amber)]/[0.06] p-4" data-testid="zevari-not-configured">
                            <div className="flex items-start gap-2.5">
                                <AlertTriangle size={15} className="text-[var(--jr-amber)] mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-sm text-[var(--jr-text-2)]">{t("signals.zev_not_configured")}</p>
                                    <Link to="/app/integrations" onClick={onClose}
                                        className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium hover:underline" style={{ color: ZEV }}>
                                        <ExternalLink size={11} /> {t("signals.zev_open_integrations")}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    ) : showStatus ? (
                        /* ---------------- Campaign status view ---------------- */
                        <div className="space-y-4" data-testid="zevari-campaign-status">
                            <div className="rounded-xl border p-4" style={{ borderColor: `${ZEV}33`, background: `${ZEV}08` }}>
                                <div className="flex items-center justify-between gap-2">
                                    <div className="text-sm font-medium text-white truncate">{campaign.name}</div>
                                    <span data-testid="zevari-campaign-status-badge"
                                        className={`text-[9px] font-mono uppercase tracking-[0.16em] px-2 py-0.5 rounded-full ${
                                        campaign.status === "active"
                                            ? "bg-[var(--jr-success)]/15 text-[var(--jr-success)]"
                                            : "bg-[var(--jr-amber)]/15 text-[var(--jr-amber)]"}`}>
                                        {campaign.status === "active" ? t("signals.zev_status_active") : t("signals.zev_status_pending")}
                                    </span>
                                </div>
                                <div className="mt-2 flex items-center gap-3 text-[11px] text-[var(--jr-text-3)] font-mono flex-wrap">
                                    <span className="inline-flex items-center gap-1">
                                        {campaign.campaign_type === "warm_up" ? <Flame size={11} /> : <Send size={11} />}
                                        {campaign.campaign_type === "warm_up" ? t("signals.zev_type_warmup") : t("signals.zev_type_outreach")}
                                    </span>
                                    <span className="inline-flex items-center gap-1"><Users size={11} /> {campaign.target_count} targets</span>
                                    {campaign.skipped_count > 0 && (
                                        <span className="text-[var(--jr-amber)]">{t("signals.zev_skipped", { n: campaign.skipped_count })}</span>
                                    )}
                                </div>
                            </div>

                            <div className="rounded-xl border border-white/8 bg-[var(--jr-surface-2)]/50 p-4">
                                <div className="flex items-start gap-2.5">
                                    <ShieldCheck size={15} className="mt-0.5 shrink-0" style={{ color: ZEV }} />
                                    <p className="text-xs text-[var(--jr-text-2)] leading-relaxed">{t("signals.zev_approval_explain")}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 flex-wrap">
                                {campaign.approval_url && campaign.status !== "active" && (
                                    <a href={campaign.approval_url} target="_blank" rel="noreferrer" data-testid="zevari-approval-link"
                                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold text-black transition-opacity hover:opacity-90"
                                        style={{ background: ZEV }}>
                                        <ExternalLink size={12} /> {t("signals.zev_approve_btn")}
                                    </a>
                                )}
                                {campaign.status !== "active" && (
                                    <button onClick={activate} disabled={activating} data-testid="zevari-activate-btn"
                                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium border transition-colors disabled:opacity-50"
                                        style={{ borderColor: `${ZEV}55`, color: ZEV }}>
                                        {activating ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                                        {t("signals.zev_activate_btn")}
                                    </button>
                                )}
                                <button onClick={() => { setShowForm(true); }} data-testid="zevari-new-campaign-btn"
                                    className="px-4 py-2 rounded-full text-xs text-[var(--jr-text-3)] hover:text-white border border-white/10 hover:bg-white/5 transition-colors">
                                    {t("signals.zev_new_campaign")}
                                </button>
                            </div>
                        </div>
                    ) : targets.length === 0 ? (
                        <div className="rounded-xl border border-[var(--jr-amber)]/30 bg-[var(--jr-amber)]/[0.06] p-4 flex items-start gap-2.5" data-testid="zevari-no-targets">
                            <AlertTriangle size={15} className="text-[var(--jr-amber)] mt-0.5 shrink-0" />
                            <p className="text-sm text-[var(--jr-text-2)]">{t("signals.zev_no_targets")}</p>
                        </div>
                    ) : (
                        /* ---------------- Create campaign form ---------------- */
                        <div className="space-y-4" data-testid="zevari-campaign-form">
                            {/* Targets preview */}
                            <div className="rounded-xl border border-white/8 bg-[var(--jr-surface-2)]/50 p-4">
                                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">
                                    <Users size={11} style={{ color: ZEV }} />
                                    {t("signals.zev_targets_found", { n: Math.min(info.count, info.max_targets) })}
                                    {info.count > info.max_targets && (
                                        <span className="normal-case tracking-normal">· {t("signals.zev_cap_note", { max: info.max_targets })}</span>
                                    )}
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {targets.slice(0, 8).map((tg, i) => (
                                        <span key={i} className="text-[10px] px-2 py-1 rounded-full border border-white/10 bg-white/5 text-[var(--jr-text-2)]"
                                            data-testid={`zevari-target-${i}`}>
                                            {tg.name}{tg.company ? ` · ${tg.company}` : ""}
                                        </span>
                                    ))}
                                    {targets.length > 8 && (
                                        <span className="text-[10px] px-2 py-1 text-[var(--jr-text-3)]">+{targets.length - 8}</span>
                                    )}
                                </div>
                            </div>

                            {/* Name */}
                            <div>
                                <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{t("signals.zev_name_label")}</label>
                                <input value={name} onChange={(e) => setName(e.target.value)} data-testid="zevari-name-input"
                                    className="mt-1 w-full rounded-lg border border-white/10 bg-[var(--jr-surface-2)] px-3 py-2 text-sm text-white outline-none focus:border-[#38bdf8]/50" />
                            </div>

                            {/* Type */}
                            <div>
                                <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{t("signals.zev_type_label")}</label>
                                <div className="mt-1 grid sm:grid-cols-2 gap-2">
                                    <button onClick={() => setType("warm_up")} data-testid="zevari-type-warmup"
                                        className={`text-left rounded-xl border p-3 transition-colors ${type === "warm_up" ? "bg-[#38bdf8]/10" : "border-white/10 hover:bg-white/5"}`}
                                        style={type === "warm_up" ? { borderColor: `${ZEV}66` } : {}}>
                                        <div className="flex items-center gap-1.5 text-sm font-medium text-white"><Flame size={13} style={{ color: ZEV }} /> {t("signals.zev_type_warmup")}</div>
                                        <p className="text-[10px] text-[var(--jr-text-3)] mt-1">{t("signals.zev_type_warmup_desc")}</p>
                                    </button>
                                    <button onClick={() => setType("outreach")} data-testid="zevari-type-outreach"
                                        className={`text-left rounded-xl border p-3 transition-colors ${type === "outreach" ? "bg-[#38bdf8]/10" : "border-white/10 hover:bg-white/5"}`}
                                        style={type === "outreach" ? { borderColor: `${ZEV}66` } : {}}>
                                        <div className="flex items-center gap-1.5 text-sm font-medium text-white"><Send size={13} style={{ color: ZEV }} /> {t("signals.zev_type_outreach")}</div>
                                        <p className="text-[10px] text-[var(--jr-text-3)] mt-1">{t("signals.zev_type_outreach_desc")}</p>
                                    </button>
                                </div>
                            </div>

                            {/* Connection note */}
                            <div>
                                <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">
                                    {t("signals.zev_note_label")} <span className="text-[var(--jr-text-3)]/60 normal-case tracking-normal">({note.length}/300)</span>
                                </label>
                                <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 300))} rows={2} data-testid="zevari-note-input"
                                    placeholder={t("signals.zev_note_hint")}
                                    className="mt-1 w-full rounded-lg border border-white/10 bg-[var(--jr-surface-2)] px-3 py-2 text-sm text-white outline-none focus:border-[#38bdf8]/50 resize-none" />
                            </div>

                            {/* Outreach follow-up message */}
                            {type === "outreach" && (
                                <div>
                                    <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{t("signals.zev_message_label")}</label>
                                    <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} data-testid="zevari-message-input"
                                        placeholder={t("signals.zev_message_hint")}
                                        className="mt-1 w-full rounded-lg border border-white/10 bg-[var(--jr-surface-2)] px-3 py-2 text-sm text-white outline-none focus:border-[#38bdf8]/50 resize-none" />
                                </div>
                            )}

                            {/* Approval gate reassurance */}
                            <div className="rounded-lg border border-white/8 bg-[var(--jr-surface-2)]/40 px-3 py-2.5 flex items-start gap-2">
                                <ShieldCheck size={13} className="mt-0.5 shrink-0" style={{ color: ZEV }} />
                                <p className="text-[11px] text-[var(--jr-text-3)] leading-relaxed">{t("signals.zev_gate_note")}</p>
                            </div>

                            <div className="flex items-center justify-end gap-2 pt-1">
                                {campaign && (
                                    <button onClick={() => setShowForm(false)}
                                        className="px-4 py-2 rounded-full text-xs text-[var(--jr-text-3)] hover:text-white border border-white/10 hover:bg-white/5">
                                        {t("common.cancel")}
                                    </button>
                                )}
                                <button onClick={create} data-testid="zevari-create-btn"
                                    disabled={busy || !name.trim() || (type === "outreach" && !message.trim())}
                                    className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-xs font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-40"
                                    style={{ background: ZEV }}>
                                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Rocket size={12} />}
                                    {busy ? t("signals.zev_creating") : t("signals.zev_create_btn")}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
